import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { AssignDialog } from "@/components/AssignDialog";
import { toast } from "sonner";
import { Plus, Undo2, Trash2, Loader2 } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Rentals() {
  const qc = useQueryClient();
  const [tab, setTab] = useState("all");
  const [assignOpen, setAssignOpen] = useState(false);
  const [returnId, setReturnId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const { data: rentals = [], isLoading } = useQuery({
    queryKey: ["rentals"],
    queryFn: async () => (await api.get("/rentals")).data,
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: items = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data });

  const returnMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/rentals/${id}/return`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rentals"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Return logged — unit back in stock");
      setReturnId(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/rentals/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rentals"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      toast.success("Record deleted");
      setDeleteId(null);
    },
  });

  const availableItems = items.filter((i) => i.status === "available");
  const filtered = useMemo(() => {
    if (tab === "all") return rentals;
    if (tab === "active") return rentals.filter((r) => r.status === "active" || r.status === "overdue");
    return rentals.filter((r) => r.status === tab);
  }, [rentals, tab]);

  return (
    <div data-testid="rentals-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-[#0F172A]">Deliveries &amp; Returns</h1>
          <p className="text-slate-500 mt-1">Track which unit is with which client.</p>
        </div>
        <Button onClick={() => setAssignOpen(true)} className="bg-[#0A2540] hover:bg-[#1E3A5F]" data-testid="new-delivery-button">
          <Plus className="h-4 w-4 mr-1.5" /> New Delivery
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="rentals-filter-tabs">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="overdue">Overdue</TabsTrigger>
          <TabsTrigger value="returned">Returned</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="border-slate-200 shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>Asset Tag</TableHead>
                <TableHead>Device</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Delivered</TableHead>
                <TableHead>Expected Return</TableHead>
                <TableHead>Returned</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#0A2540]" /></TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">No deliveries here yet.</TableCell></TableRow>
              ) : (
                filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`rental-row-${r.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-mono font-medium text-[#0F172A]">{r.asset_tag}</TableCell>
                    <TableCell className="text-slate-600">{r.device_label}</TableCell>
                    <TableCell>
                      <div className="font-medium text-[#0F172A]">{r.client_name}</div>
                      {r.client_company && <div className="text-xs text-slate-500">{r.client_company}</div>}
                    </TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.delivery_date)}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.expected_return_date)}</TableCell>
                    <TableCell className="text-slate-600">{fmtDate(r.actual_return_date)}</TableCell>
                    <TableCell><StatusBadge status={r.status} testid={`rental-status-${r.id}`} /></TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {r.status !== "returned" && (
                          <Button size="sm" variant="outline" className="h-8" onClick={() => setReturnId(r.id)} data-testid={`return-${r.id}`}>
                            <Undo2 className="h-3.5 w-3.5 mr-1" /> Return
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setDeleteId(r.id)} data-testid={`delete-rental-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AssignDialog open={assignOpen} onOpenChange={setAssignOpen} clients={clients} availableItems={availableItems} />

      <AlertDialog open={!!returnId} onOpenChange={(o) => !o && setReturnId(null)}>
        <AlertDialogContent data-testid="return-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as returned?</AlertDialogTitle>
            <AlertDialogDescription>This logs today's date as the return and puts the unit back to Available.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-[#0A2540] hover:bg-[#1E3A5F]" onClick={() => returnMutation.mutate(returnId)} data-testid="return-confirm">Confirm Return</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-rental-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>If active, the unit will be returned to Available.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => deleteMutation.mutate(deleteId)} data-testid="delete-rental-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
